/**
 * Deterministic seeded PRNG (mulberry32). The sim core must never use
 * Math.random() or Date.now(); all randomness flows through Rng instances
 * derived from the episode seed.
 */

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class Rng {
  private readonly next01: () => number;

  constructor(seed: number) {
    this.next01 = mulberry32(seed);
  }

  /** Uniform in [0, 1). */
  next(): number {
    return this.next01();
  }

  /** Uniform in [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next01();
  }

  /** Uniform integer in [0, n). */
  int(n: number): number {
    return Math.floor(this.next01() * n);
  }
}

/** Derive a decorrelated child seed (e.g. for the scripted `random` player). */
export function deriveSeed(seed: number, salt: number): number {
  return ((seed ^ 0x9e3779b9) + Math.imul(salt, 0x85ebca6b)) >>> 0;
}
